import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, Stack, TextField, Typography, InputAdornment, IconButton, Tooltip } from '@mui/material';
// icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from 'common/customIcons/search';
// utils and helpers
import { debounce } from "utilities/common";
import { getDistributionConfig, getWasteInventory } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from 'layouts/DashboardLayout'
import MDButton from 'components/MDButton';
import { Transition } from 'components/distributionSetting/distributionManagement/distributionDetails';
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import { DiscrepancyTypeFilter, SubCategoryFilter, BrandFilter, StatusFilter } from "common/filters";
import WasteInventorySKU from "common/CommonDrawer/hyperwolf/wasteInventorySKU";
import { formatTimestampToWithoutTime } from "utilities/common";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Pending Approval', value: 'pendingApproval' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Rejected', value: 'rejected' },
]

const WasteInventory = () => {
  const dispatch = useDispatch();

  const [showDetails, setShowDetails] = useState(null)
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTable, setRefreshTable] = useState(false);
  const [customFilters, setCustomFilters] = useState({});
  const [distributionConfigs, setDistributionConfigs] = useState({});

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Product Name/SKU',
        field: 'productName',
        suppressMenu: false,
        flex: 2,
        minWidth: 280,
        sortable: false,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { productName, sku, assets } = params?.data || {};
          const { thumbURL, origURL, name } = assets || {}

          return <Box style={{ display: "flex", gap: "10px" }}>
            {(thumbURL || origURL) && <img src={thumbURL || origURL} alt={name} width={40} height={40} />}
            <Box>
              <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{productName}</Typography>
              {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
            </Box>
          </Box>
        }
      },
      {
        headerName: 'Brand',
        field: 'brand',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 160,
        sortable: false,
        valueGetter: ({ data }) => data?.brand?.name || "-",
      },
      {
        headerName: 'Batch',
        field: 'batch',
        suppressMenu: false,
        flex: 1,
        minWidth: 130,
        sortable: false,
        cellRenderer: ({ data }) => {
          const batches = data?.discrepancyBatches || [];
          const firstFive = batches.slice(0, 2);
          const rest = batches.slice(2);

          return (
            <Box>
              {firstFive.map((batch, idx) => (
                <Typography
                  key={batch?.batchNo + idx}
                  variant='body2'
                  style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
                >
                  {batch?.sku} (Qty {batch?.qty})
                </Typography>
              ))}
              {rest.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      {rest.map((batch, idx) => (
                        <Typography
                          key={batch?.batchNo + idx}
                          variant='body2'
                          style={{ color: "#ffffff", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
                        >
                          {batch?.sku} (Qty {batch?.qty})
                        </Typography>
                      ))}
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Typography
                    variant='body2'
                    style={{
                      color: "#2E2E3A",
                      fontWeight: "500",
                      fontSize: "12px",
                      padding: "0",
                      cursor: "pointer",
                      textDecoration: "underline",
                      lineHeight: "normal"
                    }}
                  >
                    +{rest.length} more
                  </Typography>
                </Tooltip>
              )}
            </Box>
          );
        }
      },
      {
        headerName: 'Sub Category',
        field: 'category',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 160,
        sortable: false,
        valueGetter: ({ data }) => data?.category?.name || "-",
      },
      {
        headerName: 'Qty Waste',
        field: 'wasteQty',
        suppressMenu: false,
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        headerName: 'Date Marked',
        field: 'weight',
        suppressMenu: false,
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: ({ data }) => formatTimestampToWithoutTime(data?.createdDate) || "-",
      },
      {
        headerName: 'Discrepancy',
        field: 'discrepancy',
        suppressMenu: false,
        flex: 1.15,
        minWidth: 160,
        sortable: false,
        cellRenderer: ({ data = {} }) => {
          return <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>
            <Link to={`/hyperwolf/discrepancy-management?discrepancy=${data?.discrepancyId}`} style={{ fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                <g clipPath="url(#clip0_17646_26061)">
                  <path d="M8.66665 12.5H1.99999C1.46573 12.5 0.963502 12.2919 0.585767 11.9142C0.208033 11.5364 0 11.0342 0 10.5V3.83333C0 3.2991 0.208057 2.79687 0.585791 2.41913C0.963525 2.0414 1.46576 1.83334 2.00001 1.83334H4.66667C5.03488 1.83334 5.33334 2.1318 5.33334 2.50001C5.33334 2.86822 5.03488 3.16668 4.66667 3.16668H1.99999C1.8219 3.16668 1.65451 3.23603 1.52861 3.36193C1.40272 3.48783 1.33334 3.65524 1.33334 3.83333V10.5C1.33334 10.6781 1.40269 10.8455 1.52861 10.9714C1.65451 11.0973 1.8219 11.1667 1.99999 11.1667H8.66662C8.84471 11.1667 9.0121 11.0973 9.13802 10.9714C9.26392 10.8455 9.33327 10.6781 9.33327 10.5V7.83333C9.33327 7.46512 9.63175 7.16668 9.99996 7.16668C10.3682 7.16668 10.6667 7.46514 10.6667 7.83333V10.5C10.6667 11.0342 10.4586 11.5364 10.0808 11.9142C9.70309 12.2919 9.20083 12.5 8.66665 12.5ZM4.66667 8.49998C4.49604 8.49998 4.32543 8.43491 4.19527 8.30473C3.93493 8.04439 3.93493 7.62225 4.19527 7.36191L9.72381 1.83334H7.33333C6.96512 1.83334 6.66668 1.53488 6.66668 1.16667C6.66668 0.798461 6.96512 0.5 7.33333 0.5H11.3333C11.4255 0.5 11.5133 0.518724 11.5932 0.552561C11.6678 0.584094 11.7378 0.629785 11.799 0.689659L11.7991 0.689705C11.7995 0.690148 11.8 0.690567 11.8004 0.69101C11.8005 0.691126 11.8007 0.691266 11.8008 0.691382C11.8011 0.691708 11.8015 0.692058 11.8018 0.692407C11.802 0.69264 11.8022 0.692849 11.8025 0.693082C11.8027 0.693338 11.803 0.693618 11.8032 0.693827C11.8037 0.694293 11.8042 0.694782 11.8047 0.695271C11.8051 0.695737 11.8056 0.696249 11.8061 0.696715C11.8064 0.696948 11.8066 0.697251 11.8068 0.69746C11.8071 0.697693 11.8073 0.697903 11.8075 0.698136C11.8079 0.698485 11.8082 0.698811 11.8085 0.699184C11.8086 0.699277 11.8088 0.69944 11.8089 0.699556C11.8093 0.699999 11.8098 0.700441 11.8102 0.700884L11.8102 0.70093C11.8701 0.762225 11.9158 0.832229 11.9473 0.906751C11.9812 0.98663 11.9999 1.07443 11.9999 1.16665V5.16665C11.9999 5.53486 11.7014 5.83332 11.3332 5.83332C10.965 5.83332 10.6666 5.53486 10.6666 5.16665V2.77616L5.13795 8.30475C5.00789 8.43493 4.83728 8.49998 4.66667 8.49998Z" fill="#2E2E3A" />
                </g>
                <defs>
                  <clipPath id="clip0_17646_26061">
                    <rect width="12" height="12" fill="white" transform="translate(0 0.5)" />
                  </clipPath>
                </defs>
              </svg>
              <u>Link</u>
            </Link>
          </Typography>
        },
      },
      {
        headerName: 'Discrepancy Type',
        field: 'discrepancyType',
        suppressMenu: false,
        flex: 1.15,
        minWidth: 160,
        sortable: false,
        cellRenderer: ({ data = {} }) => {
          return <Box display="flex" alignItems="center" gap={0.5} className="text-discrepancy">
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
              <path d="M7.88912 3.12793C9.04623 2.89781 10.2454 3.0163 11.3354 3.46777C12.4255 3.91928 13.3576 4.68403 14.0131 5.66504C14.6684 6.64587 15.018 7.79896 15.018 8.97852C15.0162 10.5601 14.3874 12.077 13.269 13.1953C12.1507 14.3136 10.6337 14.9425 9.0522 14.9443C7.87252 14.9442 6.71961 14.5939 5.73873 13.9385C4.75774 13.283 3.99298 12.3517 3.54146 11.2617C3.09001 10.1718 2.97157 8.97247 3.20162 7.81543C3.43179 6.65829 4.00021 5.59501 4.83443 4.76074C5.66871 3.92646 6.73193 3.35811 7.88912 3.12793ZM9.0522 11.1484C8.89156 11.1485 8.73463 11.1959 8.60103 11.2852C8.46726 11.3745 8.3628 11.5017 8.30123 11.6504C8.2397 11.799 8.22395 11.9624 8.25533 12.1201C8.28671 12.2779 8.36327 12.4233 8.47701 12.5371C8.59078 12.6509 8.7362 12.7284 8.894 12.7598C9.05171 12.7911 9.21516 12.7744 9.36373 12.7129C9.51237 12.6513 9.63958 12.5469 9.72896 12.4131C9.81818 12.2795 9.8656 12.1226 9.86568 11.9619C9.86568 11.7464 9.78065 11.5392 9.62838 11.3867C9.47582 11.2342 9.26795 11.1484 9.0522 11.1484ZM9.0522 5.18262C8.90855 5.18275 8.77098 5.24021 8.66939 5.3418C8.56782 5.44342 8.51029 5.58093 8.51021 5.72461V9.52148C8.51031 9.66515 8.56781 9.80269 8.66939 9.9043C8.77098 10.0059 8.90855 10.0633 9.0522 10.0635C9.19604 10.0635 9.33429 10.006 9.43599 9.9043C9.53757 9.80269 9.59507 9.66515 9.59517 9.52148V5.72461C9.59509 5.58093 9.53757 5.44342 9.43599 5.3418C9.33429 5.24009 9.19604 5.18262 9.0522 5.18262ZM9.40181 1.9375C11.1443 2.02383 12.798 2.75377 14.0376 3.99316C15.1511 5.10676 15.8514 6.55532 16.0454 8.10156C15.8501 6.55608 15.1487 5.10926 14.0356 3.99609C12.7964 2.7569 11.1438 2.02556 9.40181 1.9375Z" fill="#FD4438" />
            </svg>
            <Typography color="#FD4438" fontWeight="600" style={{ fontSize: "12px" }}>{data?.discrepancyType}</Typography>
          </Box>
        },
      },
      {
        headerName: 'Distribution ID',
        field: 'distributionType',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 160,
        sortable: false,
      },
      {
        headerName: 'Kit Value',
        field: 'wholeSaleValue',
        suppressMenu: false,
        flex: 1.25,
        minWidth: 160,
        sortable: false,
        valueGetter: ({ data }) => data?.wholeSaleValue?.toString() ? `$${data?.wholeSaleValue?.toLocaleString()}` : "-"
      },
      {
        headerName: 'Value',
        field: 'totalPrice',
        suppressMenu: false,
        flex: 1,
        minWidth: 120,
        sortable: false,
        valueGetter: ({ data }) => data?.totalPrice?.toString() ? `$${data?.totalPrice?.toLocaleString()}` : "-"
      },
      {
        headerName: 'Action',
        field: 'action',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 180,
        sortable: false,
        cellStyle: { textAlign: 'right', justifyContent: 'end' },
        headerClass: 'right-aligned-cell-header',
        cellRenderer: ({ data = {} }) => {
          const { status } = data || {};
          return <MDButton
            onClick={() => {
              setShowDetails(data);
            }}
            size='small'
            startIcon={<VisibilityIcon fontSize='small' width='14' height='14' />} variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
            style={{ fontSize: "12px", minHeight: "auto" }}
          >
            {status === "pending" ? "View and Verify" : "View"}
          </MDButton>
        },
      },
    ];

    // if (distributionConfigs.isRetailer) {
    //     columns.splice(9, 0, {
    //         headerName: 'Retail Value',
    //         field: 'retailValue',
    //         suppressMenu: false,
    //         flex: 1.25,
    //         minWidth: 140,
    //         sortable: false,
    //         valueGetter: ({ data }) => data?.retailValue?.toString() ? `$${data?.retailValue?.toLocaleString()}` : "-"
    //     })
    // }

    return columns;
  }, [distributionConfigs])

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
    }
  }

  const handleDebounceSearch = useCallback(debounce((event) => {
    setCustomFilters(prev => ({ ...prev, search: event.target.value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleChange = (e) => {
    const { value, name } = e.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(!refreshTable);
  };

  const handleClearFilters = () => {
    setCustomFilters({ search: customFilters.search || "" })
    setRefreshTable(prev => !prev)
  };

  useEffect(() => {
    handleGetConfigs()
  }, [])

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">Waste Inventory</Typography>
          </Grid>

          {/* Search Filter */}
          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
              {/* Search filter */}
              <TextField
                className="custom-search-field"
                placeholder="Search"
                value={searchTerm || ""}
                onChange={e => {
                  const { value } = e.target;
                  setSearchTerm(value);
                  handleDebounceSearch(e);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <IconButton size='small'>
                      <SearchIcon width='18px' height='18px' />
                    </IconButton>
                  </InputAdornment>
                }}
                style={{ minWidth: "300px" }}
              />
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={2.5} xxxl={2} className='p-remove'>
              <DiscrepancyTypeFilter
                value={customFilters?.discrepancyType}
                handleChange={handleChange}
                placeholder={"Discrepancy Type"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2} className='p-remove'>
              <SubCategoryFilter
                value={customFilters?.categoryId}
                handleChange={handleChange}
                placeholder={"Categories"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2}>
              <BrandFilter
                value={customFilters?.brandId || ""}
                handleChange={handleChange}
                placeholder={"Brands"}
                selectAllLabel={"All brands"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2}>
              <StatusFilter
                value={customFilters?.status || ""}
                handleChange={handleChange}
                placeholder={"Status"}
                selectAllLabel={"All"}
                options={statusOptions}
              />
            </Grid>
            <Grid item xs={12} lg={2} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Listing */}
        <div
          className="ag-theme-quartz table-nodata-size header-ag-grid-lines"
          style={{ height: "60vh", width: "100%" }}
        >
          <CommonAgGridTable
            pagination={false}
            columnDefs={columnDefs}
            getDataMethod={getWasteInventory}
            refreshTable={refreshTable}
            customFilters={customFilters}
            onCellClicked={(props) => {
              // Prevent row click if click originated from the actions column
              if (props?.column && (["actions"].includes(props.column.getColId()))) {
                return;
              }
              setShowDetails(props?.data);
            }}
          />
        </div>

        {/* Details drawer */}
        <WasteInventorySKU
          open={showDetails}
          TransitionComponent={Transition}
          handleClose={() => setShowDetails(null)}
        />
      </Box>
    </CommonLayout>
  )
}

export default WasteInventory
